import { useReadQuery } from "@apollo/client";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { DefaultizedPieValueType } from "@mui/x-charts";
import DonutChart from "components/Dashboard/DonutChart";
import { TruncateTypography } from "components/TruncateTypography";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import type { ReactElement } from "react";
import { useCallback, useMemo, useState } from "react";
import { useWasteOverviewState } from "../../WasteOverviewContext";
import { smallDonutProps, vendorColorPalette } from "./constants";

const INITIAL_VISIBLE_ITEMS = 5;

type VendorChartProps = {
  queryData?: {
    wasteVendorTotals?: Array<{
      manifestSource: string;
      totalQuantityPounds: number;
    }>;
  };
};

type VendorDataItem = {
  manifestSource: string;
  totalQuantityPounds: number;
};

type ChartDataItem = {
  id: string;
  label: (location: "tooltip" | "legend" | "arc") => string;
  value: number;
  vendorName: string;
  color: string;
};

type LegendDataItem = {
  color: string;
  value: string | ReactElement;
  tooltip?: string;
};

type ProcessedData = {
  chartData: ChartDataItem[];
  legendData: LegendDataItem[];
  totalAmount: number;
  hasUnknown: boolean;
  allVendors: VendorDataItem[];
};

function createChartDataItem(
  item: VendorDataItem,
  index: number,
  totalAmount: number
): ChartDataItem {
  const percentage = ((item.totalQuantityPounds / totalAmount) * 100).toFixed(
    1
  );
  const formattedAmount = item.totalQuantityPounds.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });

  return {
    id: item.manifestSource,
    label: (location: "tooltip" | "legend" | "arc") => {
      if (location === "tooltip") {
        return `${prettyPrintEnumValue(
          item.manifestSource
        )}: ${formattedAmount} lbs (${percentage}%)`;
      }
      return item.manifestSource;
    },
    value: item.totalQuantityPounds,
    vendorName: item.manifestSource,
    color: vendorColorPalette[index % vendorColorPalette.length],
  };
}

function createLegendDataItem(
  item: VendorDataItem,
  totalAmount: number,
  chartDataIndex: number,
  selectedIndex: number | null
): LegendDataItem {
  const color = vendorColorPalette[chartDataIndex % vendorColorPalette.length];
  const percentage = (item.totalQuantityPounds / totalAmount) * 100;
  const displayPercentage = percentage >= 1 ? percentage.toFixed(0) : "<1";
  const formattedAmount = Math.round(item.totalQuantityPounds).toLocaleString();
  const isSelected = selectedIndex === null || selectedIndex === chartDataIndex;

  return {
    color,
    value: (
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        gap={2}
        sx={{
          opacity: isSelected ? 1 : 0.3,
        }}
      >
        <TruncateTypography variant="body2" sx={{ flexGrow: 1, mr: 1 }}>
          {prettyPrintEnumValue(item.manifestSource)}
        </TruncateTypography>
        <Typography
          variant="body2"
          sx={{ textAlign: "right", whiteSpace: "nowrap" }}
        >
          {formattedAmount} lbs{" "}
          {item.totalQuantityPounds !== 0 ? `(${displayPercentage}%)` : ""}
        </Typography>
      </Box>
    ),
    tooltip: prettyPrintEnumValue(item.manifestSource),
  };
}

function processVendorData(
  queryData: VendorChartProps["queryData"],
  selectedIndex: number | null,
  showAll: boolean
): ProcessedData {
  const rawData = queryData?.wasteVendorTotals ?? [];

  if (rawData.length === 0) {
    return {
      chartData: [],
      legendData: [],
      totalAmount: 0,
      hasUnknown: false,
      allVendors: [],
    };
  }

  // Calculate total for percentages
  const totalAmount = rawData.reduce(
    (sum: number, item) => sum + item.totalQuantityPounds,
    0
  );

  // Data is already sorted by backend and includes "Unknown" and "Other" entries
  const chartData: ChartDataItem[] = rawData.map((item, index) => {
    // Special handling for "Other" category color
    const color =
      item.manifestSource === "Other"
        ? grey[400]
        : vendorColorPalette[index % vendorColorPalette.length];

    if (totalAmount === 0 && rawData.length === 1) {
      return {
        ...createChartDataItem(item, index, totalAmount),
        // we still want the chart to show if there is one vendor with 0 pounds
        value: 1,
        color,
      };
    }

    return {
      ...createChartDataItem(item, index, totalAmount),
      color,
    };
  });

  // Show top INITIAL_VISIBLE_ITEMS or all based on showAll state
  const legendDisplayLimit = showAll ? rawData.length : INITIAL_VISIBLE_ITEMS;
  let legendEntries = rawData.slice(0, legendDisplayLimit);

  // If not expanded and user clicked on an entry not in top INITIAL_VISIBLE_ITEMS, add it to legend
  if (
    !showAll &&
    selectedIndex !== null &&
    selectedIndex >= INITIAL_VISIBLE_ITEMS
  ) {
    const selectedVendor = rawData[selectedIndex];
    if (selectedVendor) {
      // Check if selected vendor is not already in the visible entries
      const isInVisible = legendEntries.some(
        (entry) => entry.manifestSource === selectedVendor.manifestSource
      );
      if (!isInVisible) {
        legendEntries = [...legendEntries, selectedVendor];
      }
    }
  }

  // Legend data - only visible items based on showAll state
  const legendData: LegendDataItem[] = legendEntries.map((item) => {
    // Find the original index in rawData to maintain consistent colors
    const originalIndex = rawData.findIndex(
      (rawItem) => rawItem.manifestSource === item.manifestSource
    );
    const color =
      item.manifestSource === "Other"
        ? grey[400]
        : vendorColorPalette[originalIndex % vendorColorPalette.length];

    return {
      ...createLegendDataItem(item, totalAmount, originalIndex, selectedIndex),
      color,
    };
  });

  const hasUnknown = rawData.some((item) => item.manifestSource === "Unknown");

  return {
    chartData,
    legendData,
    totalAmount,
    hasUnknown,
    allVendors: rawData,
  };
}

export function VendorChart() {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const theme = useTheme();

  const { vendorQuery } = useWasteOverviewState();

  const { data } = useReadQuery(vendorQuery);

  const processedData = useMemo(
    () => processVendorData(data, selectedIndex, showAll),
    [data, selectedIndex, showAll]
  );

  const { chartData, legendData, allVendors } = processedData;

  const handleItemClick = (item: DefaultizedPieValueType) => {
    const clickedIndex = chartData.findIndex((d) => d.id === item.id);
    // If the index is already selected, set it to null
    if (selectedIndex === clickedIndex) {
      setSelectedIndex(null);
    } else {
      setSelectedIndex(clickedIndex);
    }
  };

  const handleLegendItemClick = (item: DefaultizedPieValueType) => {
    // Find the corresponding chart data index
    const chartIndex = chartData.findIndex((d) => d.id === item.id);

    if (chartIndex !== -1) {
      if (selectedIndex === chartIndex) {
        setSelectedIndex(null);
      } else {
        setSelectedIndex(chartIndex);
      }
    }
  };

  const handleExpandButtonClick = useCallback(() => {
    setShowAll(!showAll);
    setSelectedIndex(null);
  }, [showAll]);

  const shouldShowExpandButton = allVendors.length > INITIAL_VISIBLE_ITEMS;

  const isLg = useMediaQuery(theme.breakpoints.down("lg"));
  const isMd = useMediaQuery(theme.breakpoints.down("md"));
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const isEmpty = chartData.length === 0;

  return (
    <DonutChart
      {...smallDonutProps}
      seriesData={
        chartData.length > 0
          ? chartData
          : [
              {
                id: "no-vendors",
                value: 1,
                label: () => "🚛 No Vendors to show",
                color: grey[300],
              },
            ]
      }
      selectedIndex={selectedIndex}
      onItemClick={(event, pieItemIdentifier, item) => handleItemClick(item)}
      onLegendItemClick={handleLegendItemClick}
      legendItems={
        chartData.length > 0
          ? legendData
          : [{ value: "🚛 No Vendors to show", color: "white" }]
      }
      colors={vendorColorPalette}
      legend={{
        position: isLg ? (isMd ? "right" : "below") : "right",
        direction: "column",
        showExpandButton: shouldShowExpandButton,
        onExpandButtonClick: handleExpandButtonClick,
        expandButtonText: showAll
          ? `Collapse to Top ${INITIAL_VISIBLE_ITEMS} Vendors`
          : "Show All Vendors",
        expandButtonIcon: showAll ? <ExpandLessIcon /> : <ExpandMoreIcon />,
      }}
      legendSx={{ width: "100%", overflow: "hidden" }}
      showTooltip={true}
      showTooltipTitle={false}
      justifyContent={isEmpty || isSm ? "center" : "start"}
      alignItems={isEmpty || isSm ? "center" : "start"}
    />
  );
}
